import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { formatDocument, formatDate } from '../../shared/utils/format';
import {
  Button,
  Card,
  Badge,
  Input,
  Modal,
  LoadingSkeleton,
} from '@nexora/design-system';
import { useToast } from '@nexora/design-system';
import {
  useCustomers,
  useCreateCustomer,
  useUpdateCustomer,
  useDeleteCustomer,
  type Customer,
  type CustomerPayload,
} from './useMasterData';

/* ── Helpers ── */

const emptyForm: CustomerPayload = {
  name: '',
  documentType: 'CNPJ',
  documentNumber: '',
  email: '',
  phone: '',
};

/** CPF tem 11 digitos, CNPJ tem 14 — o tipo segue o que foi digitado. */
function documentTypeOf(value: string): 'CPF' | 'CNPJ' {
  return value.replace(/\D/g, '').length === 11 ? 'CPF' : 'CNPJ';
}

/* ── Component ── */

export function CustomersPage(): JSX.Element {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Customer | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [form, setForm] = useState<CustomerPayload>(emptyForm);

  const [search, setSearch] = useState('');

  const { data, isLoading, error } = useCustomers();

  /* O endpoint devolve o array completo (sem paginacao), entao estes numeros
     sao o total real e a busca cobre todos os registros. */
  const all = data ?? [];
  /* `isBlocked` e o campo real; "ativo" e a negacao dele. */
  const activeCount = all.filter((c) => !c.isBlocked).length;
  const withoutEmail = all.filter((c) => !c.email).length;

  const term = search.trim().toLowerCase();
  const rows = term
    ? all.filter((c) =>
        [c.name, c.documentNumber, c.email, c.phone]
          .some((v) => (v ?? '').toLowerCase().includes(term)),
      )
    : all;

  const createMutation = useCreateCustomer();
  const updateMutation = useUpdateCustomer();
  const deleteMutation = useDeleteCustomer();

  const openCreate = (): void => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (customer: Customer): void => {
    setEditing(customer);
    setForm({
      name: customer.name,
      documentType: customer.documentType ?? documentTypeOf(customer.documentNumber ?? ''),
      documentNumber: customer.documentNumber ?? '',
      ...(customer.email ? { email: customer.email } : {}),
      ...(customer.phone ? { phone: customer.phone } : {}),
    });
    setShowModal(true);
  };

  const handleSave = (): void => {
    if (!form.name) {
      addToast('O nome é obrigatório', 'warning');
      return;
    }

    if (editing) {
      updateMutation.mutate(
        { ...form, id: editing.id },
        {
          onSuccess: () => {
            addToast('Cliente atualizado com sucesso', 'success');
            setShowModal(false);
            setEditing(null);
          },
          onError: (err) => {
            addToast(err instanceof Error ? err.message : 'Erro ao atualizar cliente', 'error');
          },
        },
      );
    } else {
      createMutation.mutate(form, {
        onSuccess: () => {
          addToast('Cliente criado com sucesso', 'success');
          setShowModal(false);
        },
        onError: (err) => {
          addToast(err instanceof Error ? err.message : 'Erro ao criar cliente', 'error');
        },
      });
    }
  };

  const handleDelete = (id: string): void => {
    deleteMutation.mutate(id, {
      onSuccess: () => {
        addToast('Cliente excluído com sucesso', 'success');
        setDeleteConfirmId(null);
      },
      onError: (err) => {
        addToast(err instanceof Error ? err.message : 'Erro ao excluir cliente', 'error');
        setDeleteConfirmId(null);
      },
    });
  };

  return (
    <div className="md-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Clientes</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Clientes e seus dados cadastrais.</p>
        </div>
        <div className="flex flex-wrap items-center gap-sm">
          <Button variant="outline" onClick={openCreate}>
            Importar Clientes
          </Button>
          <Button variant="primary" onClick={() => void navigate({ to: '/master-data/customers/new' })}>
            Novo Cliente
          </Button>
        </div>
      </div>

      {/* Indicadores derivados do proprio array carregado. O prototipo traz
          "Active Leads", "Churn Rate" e "Outstanding" — nenhum tem fonte no
          contrato de Customer, entao entraram metricas reais no lugar. */}
      {!isLoading && !error && all.length > 0 && (
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-md mb-lg">
          <div className="bg-surface-container-lowest rounded-xl border border-outline-variant p-md shadow-sm">
            <div className="font-label-md text-label-md text-on-surface-variant uppercase">
              Total de clientes
            </div>
            <div className="font-display-lg text-display-lg text-on-surface tabular-nums mt-xs">
              {all.length}
            </div>
          </div>
          <div className="bg-surface-container-lowest rounded-xl border border-outline-variant p-md shadow-sm">
            <div className="font-label-md text-label-md text-on-surface-variant uppercase">
              Ativos
            </div>
            <div className="font-display-lg text-display-lg text-success-green tabular-nums mt-xs">
              {activeCount}
            </div>
          </div>
          <div className="bg-surface-container-lowest rounded-xl border border-outline-variant p-md shadow-sm">
            <div className="font-label-md text-label-md text-on-surface-variant uppercase">
              Inativos
            </div>
            <div className="font-display-lg text-display-lg text-on-surface-variant tabular-nums mt-xs">
              {all.length - activeCount}
            </div>
          </div>
          <div className="bg-surface-container-lowest rounded-xl border border-outline-variant p-md shadow-sm">
            <div className="font-label-md text-label-md text-on-surface-variant uppercase">
              Sem e-mail
            </div>
            <div
              className={`font-display-lg text-display-lg tabular-nums mt-xs ${
                withoutEmail > 0 ? 'text-warning-amber' : 'text-on-surface'
              }`}
            >
              {withoutEmail}
            </div>
            <p className="font-body-sm text-body-sm text-on-surface-variant mt-xs mb-0">
              Bloqueia envio de cobrança e de nota
            </p>
          </div>
        </div>
      )}

      <Card padding="0">
        {all.length > 0 && (
          <div className="p-md border-b border-outline-variant flex flex-wrap items-center justify-between gap-md">
            <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">
              Clientes cadastrados
            </h3>
            <div className="flex items-center gap-sm">
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar nome, documento, e-mail ou telefone..."
                aria-label="Buscar clientes"
                className="bg-surface border border-outline-variant font-body-sm text-body-sm text-on-surface rounded py-1.5 px-sm focus:ring-1 focus:ring-primary focus:border-primary w-full sm:w-80"
              />
              <span className="font-label-md text-label-md text-on-surface-variant tabular-nums whitespace-nowrap">
                {rows.length} de {all.length}
              </span>
            </div>
          </div>
        )}

        {isLoading && (
          <div className="loading-container">
            <LoadingSkeleton count={6} height="32px" />
          </div>
        )}

        {error && (
          <div className="error-container">
            <p className="error-message">Erro ao carregar clientes: {(error as Error).message}</p>
          </div>
        )}

        {!isLoading && !error && (!data || data.length === 0) && (
          <div className="empty-container">
            <p className="empty-message">Nenhum cliente cadastrado.</p>
            <Button variant="outline" size="sm" onClick={openCreate}>
              Cadastrar primeiro cliente
            </Button>
          </div>
        )}

        {!isLoading && !error && all.length > 0 && rows.length === 0 && (
          <div className="empty-container">
            <p className="empty-message">
              Nenhum cliente corresponde a &ldquo;{search}&rdquo;.
            </p>
          </div>
        )}

        {!isLoading && !error && rows.length > 0 && (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Documento</th>
                  <th>Email</th>
                  <th>Telefone</th>
                  <th>Status</th>
                  <th>Criado em</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((customer) => (
                  <tr key={customer.id}>
                    <td className="cell-name">
                      <button
                        type="button"
                        className="name-link"
                        onClick={() => void navigate({ to: `/crm/customers/${customer.id}` })}
                      >
                        {customer.name}
                      </button>
                      {customer.legalName && customer.legalName !== customer.name && (
                        <div className="cell-trading">{customer.legalName}</div>
                      )}
                    </td>
                    <td className="cell-doc">
                      {formatDocument(
                        customer.documentNumber,
                        customer.documentType as 'CPF' | 'CNPJ' | undefined,
                      )}
                    </td>
                    <td>{customer.email || '\u2014'}</td>
                    <td>{customer.phone || '\u2014'}</td>
                    <td>
                      <Badge variant={customer.isBlocked ? 'error' : 'success'}>
                        {customer.isBlocked ? 'Bloqueado' : 'Ativo'}
                      </Badge>
                    </td>
                    <td>{formatDate(customer.createdAt)}</td>
                    <td>
                      <div className="row-actions">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => void navigate({ to: `/crm/customers/${customer.id}` })}
                        >
                          Ficha
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openEdit(customer)}>
                          Editar
                        </Button>
                        {deleteConfirmId === customer.id ? (
                          <div className="inline-confirm">
                            <span className="confirm-text">Excluir?</span>
                            <Button
                              size="sm"
                              variant="danger"
                              loading={deleteMutation.isPending}
                              onClick={() => handleDelete(customer.id)}
                            >
                              Sim
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setDeleteConfirmId(null)}
                              disabled={deleteMutation.isPending}
                            >
                              Não
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDeleteConfirmId(customer.id)}
                          >
                            Excluir
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditing(null); }}
        title={editing ? 'Editar Cliente' : 'Novo Cliente'}
        footer={
          <div className="modal-actions">
            <Button variant="ghost" onClick={() => { setShowModal(false); setEditing(null); }}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!form.name || createMutation.isPending || updateMutation.isPending}
              loading={createMutation.isPending || updateMutation.isPending}
            >
              {editing ? 'Atualizar' : 'Salvar'}
            </Button>
          </div>
        }
      >
        <div className="modal-form">
          <Input
            label="Nome"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Nome completo / Razão social"
          />
          <Input
            label="Documento"
            value={form.documentNumber}
            onChange={(e) =>
              setForm({
                ...form,
                documentNumber: e.target.value,
                documentType: documentTypeOf(e.target.value),
              })
            }
            placeholder="CPF ou CNPJ"
          />
          <Input
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="email@exemplo.com"
          />
          <Input
            label="Telefone"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="(11) 99999-9999"
          />
          {/* Bloqueio de cliente nao se faz no cadastro: e transicao de estado
              com motivo, do lado do backend. O formulario deixou de oferecer um
              interruptor que nao chegava a lugar nenhum. */}
          {(createMutation.isError || updateMutation.isError) && (
            <p className="field-error">
              {((createMutation.error ?? updateMutation.error) as Error)?.message ?? ''}
            </p>
          )}
        </div>
      </Modal>

      <style>{`
        .md-page { padding: 1.5rem; }
        .loading-container { padding: 1rem; display: flex; flex-direction: column; gap: 0.75rem; }
        .error-container { padding: 2rem; text-align: center; }
        .error-message { color: #ba1a1a; font-size: 0.875rem; }
        .empty-container { padding: 2rem; text-align: center; display: flex; flex-direction: column; align-items: center; gap: 1rem; }
        .empty-message { color: #74777f; font-size: 0.875rem; margin: 0; }
        .table-wrapper { overflow-x: auto; }
        .data-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .data-table th { text-align: left; padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e; font-weight: 600; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid #e0e3e5; white-space: nowrap; position: sticky; top: 0; z-index: 1;}
        .data-table td { padding: 0.75rem 1rem; border-bottom: 1px solid #eceef0; color: #191c1e; }
        .data-table tbody tr:hover td { background: #eceef0; }
        /* A referencia usa "Client / Trading Name" em duas linhas: razao social
           em cima, nome fantasia embaixo (via legalName, que a listagem
           publica desde o todo-review7). Só renderiza quando difere do nome. */
        .cell-name { font-weight: 600; }
        .cell-trading { font-weight: 400; font-size: 0.75rem; color: #43474e; }
        .name-link { background: none; border: 0; padding: 0; font: inherit; color: inherit; cursor: pointer; text-align: left; }
        .name-link:hover { color: #002045; text-decoration: underline; }
        .cell-doc { font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; }
        .row-actions { display: flex; gap: 0.375rem; align-items: center; }
        .inline-confirm { display: flex; align-items: center; gap: 0.375rem; }
        .confirm-text { font-size: 0.8125rem; color: #ba1a1a; font-weight: 600; margin-right: 0.25rem; }
        .modal-form { display: flex; flex-direction: column; gap: 1rem; }
        .modal-actions { display: flex; gap: 0.5rem; }
        .field-error { color: #ba1a1a; font-size: 0.8125rem; margin: 0; }
        .toggle-label { display: flex; align-items: center; gap: 0.5rem; font-size: 0.875rem; color: #191c1e; cursor: pointer; }
        .toggle-label input[type="checkbox"] { width: 16px; height: 16px; cursor: pointer; }
      `}</style>
    </div>
  );
}
